import type { ApiClient } from '../client';

/**
 * Index-action shape returned by the game_platform `ShortSerializer`.
 * Used by both the classic Lobby (grid of packs) and the modern Slider
 * (per-slot pack/pedestal/logo). Each entry carries every image the
 * slider needs to render its own slot — `packLogoImageUrl` and
 * `pedestalImageUrl` come straight from the per-operator overrides.
 */
export interface PoolOfferListItem {
  id: number;
  title: string;
  /** Per-unit price as a string from the backend (e.g. `"25.0"`). */
  price: string;
  status: string;
  isMultiple: boolean;
  packImageUrl: string | null;
  packLogoImageUrl: string | null;
  pedestalImageUrl: string | null;
  suitableBadges: TierBadge[];
  /**
   * Operator-side currency snapshot from the platform session
   * (`game_platform_operator.currency`, e.g. `"EUR"`). Iframe renders this
   * alongside the USD `price` so the player sees the price in their
   * casino's currency. `null` when the backend omits the field (older
   * deploys) — caller should fall back to USD-only display.
   */
  operatorCurrency: string | null;
  /**
   * USD-per-1-operator-currency-unit rate snapshot from the operator
   * (`game_platform_operator.exchange_rate`). E.g. 1.08 means 1 EUR = 1.08 USD.
   * Combined with `price` (USD) this lets the iframe show the equivalent
   * casino-currency amount: `op_amount = price / exchange_rate`.
   * `null` when the backend omits the field.
   */
  exchangeRate: number | null;
}

interface RawIndexOffer {
  id: number | string;
  title?: string;
  name?: string;
  price?: string | number;
  status?: string;
  is_multiple?: boolean;
  pack_image?: RawImage | string | null;
  pack_image_url?: string | null;
  pack_logo_image_url?: string | null;
  pedestal_image_url?: string | null;
  suitable_badges?: RawBadge[];
  operator_currency?: string | null;
  exchange_rate?: number | string | null;
}

/**
 * Row shape for the "Repackz" tab of My Packs — one entry per pool_offer
 * the user still has unopened packs of, surfaced by the `unopened`
 * endpoint. `firstUnopenedOrderId` powers the "Open Now" button.
 */
export interface IframeUnopenedOffer {
  id: number;
  title: string;
  price: string;
  packImageUrl: string | null;
  unopenedCount: number;
  firstUnopenedOrderId: string | null;
}

interface RawUnopenedOffer {
  id?: number | string;
  title?: string;
  price?: number | string | null;
  pack_image_url?: string | null;
  unopened_count?: number | string;
  first_unopened_order_id?: number | string | null;
}

export async function getUnopenedPoolOffers(
  client: ApiClient,
): Promise<IframeUnopenedOffer[]> {
  const raw = await client.request<{ pool_offers: RawUnopenedOffer[] }>(
    '/loot_machine/api/game_platform/iframe/v1/pool_offers/unopened',
    { method: 'GET' },
  );
  return (raw.pool_offers ?? []).map((o) => ({
    id: Number(o.id ?? 0),
    title: o.title ?? '',
    price: o.price !== undefined && o.price !== null ? String(o.price) : '0',
    packImageUrl: (o.pack_image_url as string) || null,
    unopenedCount: Number(o.unopened_count ?? 0),
    firstUnopenedOrderId:
      o.first_unopened_order_id !== undefined && o.first_unopened_order_id !== null
        ? String(o.first_unopened_order_id)
        : null,
  }));
}

export interface ListPoolOffersParams {
  /** `Collectibles::Category` (sport) record id — filters to that category. */
  categoryId?: number;
  /** Defaults to `'Collectibles::Category'` when `categoryId` is set. */
  categoryClassName?: string;
  isMultiple?: boolean;
  orderBy?: 'price' | 'sales_count';
  orderDirection?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

function mapIndexOffer(o: RawIndexOffer): PoolOfferListItem {
  const fallback: RawDetail = {
    id: o.id,
    pack_image: o.pack_image ?? null,
    pack_image_url: o.pack_image_url ?? null,
  };
  return {
    id: Number(o.id ?? 0),
    title: o.title ?? o.name ?? '',
    price: o.price !== undefined && o.price !== null ? String(o.price) : '0',
    status: typeof o.status === 'string' && o.status ? o.status : 'available',
    isMultiple: o.is_multiple === true,
    packImageUrl: extractImageUrl(fallback),
    packLogoImageUrl:
      typeof o.pack_logo_image_url === 'string' && o.pack_logo_image_url
        ? o.pack_logo_image_url
        : null,
    pedestalImageUrl:
      typeof o.pedestal_image_url === 'string' && o.pedestal_image_url
        ? o.pedestal_image_url
        : null,
    suitableBadges: mapBadges(o.suitable_badges),
    operatorCurrency: extractOperatorCurrency(o.operator_currency),
    exchangeRate: extractExchangeRate(o.exchange_rate),
  };
}

/**
 * Backend ships `operator_currency` as a string (e.g. `"EUR"`) or `null`
 * on older deploys. Empty strings collapse to `null` so the iframe
 * doesn't render a "0" badge for missing config.
 */
function extractOperatorCurrency(
  v: string | null | undefined,
): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Backend ships `exchange_rate` as a JSON number (it Decimal-to-floats it
 * server-side). We accept string too so admin/test fixtures that stringify
 * the decimal still parse. Non-positive or NaN rates collapse to `null`
 * — a zero rate would blow up the `price / rate` op-currency conversion.
 */
function extractExchangeRate(
  v: number | string | null | undefined,
): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * `GET /loot_machine/api/game_platform/iframe/v1/pool_offers`
 *
 * Game-platform-scoped pack listing — the controller already applies
 * `available_for_operator(operator)`, so disabled packs in the operator
 * config never reach the client. Pass `categoryId` (+ `categoryClassName`)
 * to narrow to a single sport / collection, which is what the modern
 * Slider does once the user picks a category in the header.
 */
export async function listPoolOffers(
  client: ApiClient,
  params: ListPoolOffersParams = {},
): Promise<PoolOfferListItem[]> {
  const qs = new URLSearchParams();
  if (params.categoryId !== undefined) {
    qs.set('category_id', String(params.categoryId));
    qs.set('category_class_name', params.categoryClassName ?? 'Collectibles::Category');
  }
  if (params.isMultiple !== undefined) qs.set('is_multiple', String(params.isMultiple));
  if (params.orderBy) qs.set('order_by', params.orderBy);
  if (params.orderDirection) qs.set('order_direction', params.orderDirection);
  if (params.page !== undefined) qs.set('page', String(params.page));
  if (params.perPage !== undefined) qs.set('per_page', String(params.perPage));
  const tail = qs.toString();
  const path = `/loot_machine/api/game_platform/iframe/v1/pool_offers${tail ? '?' + tail : ''}`;
  const raw = await client.request<{ pool_offers: RawIndexOffer[] }>(path, { method: 'GET' });
  return (raw.pool_offers ?? []).map(mapIndexOffer);
}

/**
 * One entry of the tier-selector (`suitable_badges` on the backend).
 * `isSelf: true` → this tier's pool_offer_id matches the currently-viewed
 * offer, i.e. it should render as the active card.
 */
export interface TierBadge {
  poolOfferId: number;
  name: string;
  price: string;
  imageUrl: string | null;
  subImageUrl: string | null;
  isSelf: boolean;
}

/**
 * Pack-detail shape — expanded as follow-up components need more fields
 * (price, odds, rarities, videos, …).
 *
 * The shared ShowSerializer also returns `available_categories` inline, but
 * the backend guards it to `[]` in `Rails.env.development?` — we rely on the
 * separate `listAvailableCategories` endpoint instead, which works in every
 * environment.
 */
export interface PoolOfferDetail {
  id: number;
  title: string;
  /** Per-unit price as a string from the backend (e.g. `"25.0"`). */
  price: string;
  /**
   * Operator-side currency snapshot from the platform session
   * (`game_platform_operator.currency`, e.g. `"EUR"`). See
   * `PoolOfferListItem.operatorCurrency` for usage. `null` when backend
   * omits the field.
   */
  operatorCurrency: string | null;
  /**
   * USD-per-1-operator-currency-unit FX rate from the operator. See
   * `PoolOfferListItem.exchangeRate`. `null` when backend omits the field.
   */
  exchangeRate: number | null;
  packImageUrl: string | null;
  /**
   * Per-operator override surfaced by the game_platform/iframe show
   * serializer. Null when the operator has no config row or no upload —
   * the iframe falls back to bundled defaults.
   */
  packLogoImageUrl: string | null;
  /** Per-operator override of the pedestal artwork. Null when not set. */
  pedestalImageUrl: string | null;
  /**
   * Backend `pool_offer.status` — `'available'` means the pack is purchasable.
   * Anything else (e.g. `'restoring'`, `'sold_out'`, …) puts the page into a
   * read-only state with a status badge and the bottom action bar hidden.
   */
  status: string;
  /** Tier variants of this pack ("Starter / Premier / Pro" in Figma). */
  suitableBadges: TierBadge[];
  /**
   * URL to the hero-animation Lottie JSON (waves + decorative sport
   * elements). Backend integration pending — currently always null; the
   * iframe falls back to a bundled default. Reserved now so the shape stays
   * stable when the backend adds the field.
   */
  heroAnimationUrl: string | null;
}

interface RawImage {
  url?: string | null;
}

interface RawBadge {
  pool_offer_id?: number | string;
  name?: string;
  price?: string | number;
  image?: RawImage | string | null;
  sub_image_url?: string | null;
  is_self?: boolean;
}

interface RawDetail {
  id: number | string;
  title?: string;
  price?: string | number;
  status?: string | null;
  pack_image?: RawImage | string | null;
  pack_image_url?: string | null;
  pack_logo_image_url?: string | null;
  pedestal_image_url?: string | null;
  hero_animation_url?: string | null;
  suitable_badges?: RawBadge[];
  operator_currency?: string | null;
  exchange_rate?: number | string | null;
}

function extractBadgeImage(b: RawBadge): string | null {
  const i = b.image;
  if (!i) return null;
  if (typeof i === 'string') return i;
  return i.url ?? null;
}

function mapBadges(raw: RawBadge[] | undefined): TierBadge[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((b) => ({
    poolOfferId: Number(b.pool_offer_id ?? 0),
    name: b.name ?? '',
    price: b.price !== undefined && b.price !== null ? String(b.price) : '',
    imageUrl: extractBadgeImage(b),
    subImageUrl: typeof b.sub_image_url === 'string' && b.sub_image_url ? b.sub_image_url : null,
    isSelf: b.is_self === true,
  }));
}

function extractImageUrl(p: RawDetail): string | null {
  if (typeof p.pack_image_url === 'string' && p.pack_image_url) {
    return p.pack_image_url;
  }
  if (p.pack_image && typeof p.pack_image === 'object' && p.pack_image.url) {
    return p.pack_image.url;
  }
  return null;
}

/**
 * OddsPanel payload — iframe-scoped endpoint returning structured odds
 * (`tier_name` + `range` + `percentage`) plus `big_win_chance` and
 * `estimated_value`. Lives under the game_platform/iframe namespace and is
 * intentionally separate from the shared pool_offer route used for
 * hero/badges.
 */
export interface IframePoolOfferOdds {
  id: number;
  title: string;
  estimatedValue: number;
  bigWinChance: number;
  /**
   * Floor of the cheapest tier — minimum USD value a user could pull
   * from this pack. Drives the modern OddsPanel's "Min Value" stat.
   * Backend computes it via `range_lower_bound(sorted_tiers.first)`;
   * `0` when the pool offer has no tier breakdown.
   */
  minValue: number;
  maxForPurchaseCount: number;
  tiers: { tierName: string; range: string; percentage: number }[];
  /**
   * Per-operator override of the Pack-page background image. Resolved
   * server-side from `OperatorPoolOfferAsset#bg_image` for the current
   * `@platform_session.game_platform_operator`. When null, the iframe
   * falls back to the design_system gradient.
   */
  bgImageUrl: string | null;
}

interface RawOddsTier {
  tier_name?: string;
  range?: string;
  percentage?: number | string;
}

interface RawIframeOffer {
  id?: number | string;
  title?: string;
  price?: number | string | null;
  estimated_value?: number | string | null;
  big_win_chance?: number | string;
  min_value?: number | string | null;
  max_for_purchase_count?: number | string;
  odds?: RawOddsTier[];
  bg_image_url?: string | null;
}

export async function getIframePoolOfferOdds(
  client: ApiClient,
  id: string | number,
): Promise<IframePoolOfferOdds> {
  const raw = await client.request<{ pool_offer: RawIframeOffer }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  const p = raw.pool_offer ?? ({} as RawIframeOffer);
  // Mirrors the web client's `estimated_value || price` fallback so the
  // OddsPanel's Expected Value badge never shows $0.00 when the admin
  // hasn't filled the estimated_value column. See web repo
  // `src/v2/components/lootMachine/offerShow/webVersion/contentRight`.
  const evRaw = Number(p.estimated_value);
  const priceRaw = Number(p.price);
  const estimatedValue = evRaw > 0 ? evRaw : priceRaw > 0 ? priceRaw : 0;

  return {
    id: Number(p.id ?? 0),
    title: p.title ?? '',
    estimatedValue,
    bigWinChance: Number(p.big_win_chance ?? 0),
    minValue: Number(p.min_value ?? 0),
    maxForPurchaseCount: Math.max(0, Number(p.max_for_purchase_count ?? 0)),
    tiers: (p.odds ?? []).map((t) => ({
      tierName: t.tier_name ?? '',
      range: t.range ?? '',
      percentage: Number(t.percentage ?? 0),
    })),
    bgImageUrl: typeof p.bg_image_url === 'string' && p.bg_image_url ? p.bg_image_url : null,
  };
}

/**
 * Fetches `/pool_offers/:id` from the game_platform/iframe namespace. This
 * route is authenticated via `X-Platform-Session` (added automatically by
 * `createApiClient` when a session token is present) and serves a
 * per-operator override of pack assets like `pack_logo_image_url` and
 * `pedestal_image_url`.
 */
export async function getPoolOffer(
  client: ApiClient,
  id: string | number,
): Promise<PoolOfferDetail> {
  const raw = await client.request<{ pool_offer: RawDetail }>(
    `/loot_machine/api/game_platform/iframe/v1/pool_offers/${encodeURIComponent(String(id))}`,
    { method: 'GET' },
  );
  const p = raw.pool_offer ?? ({} as RawDetail);
  if (import.meta.env.DEV) {
    console.log(
      `%c[getPoolOffer] /pool_offers/${id}`,
      'color:#22E11C;font-weight:600',
      p,
    );
  }
  return {
    id: Number(p.id),
    title: p.title ?? '',
    price: p.price !== undefined && p.price !== null ? String(p.price) : '0',
    operatorCurrency: extractOperatorCurrency(p.operator_currency),
    exchangeRate: extractExchangeRate(p.exchange_rate),
    packImageUrl: extractImageUrl(p),
    packLogoImageUrl:
      typeof p.pack_logo_image_url === 'string' && p.pack_logo_image_url
        ? p.pack_logo_image_url
        : null,
    pedestalImageUrl:
      typeof p.pedestal_image_url === 'string' && p.pedestal_image_url
        ? p.pedestal_image_url
        : null,
    status: typeof p.status === 'string' && p.status ? p.status : 'available',
    suitableBadges: mapBadges(p.suitable_badges),
    heroAnimationUrl:
      typeof p.hero_animation_url === 'string' && p.hero_animation_url
        ? p.hero_animation_url
        : null,
  };
}
